/**
 * 科技蓝模板 - 共享导航栏
 * 显示后台配置的导航菜单和分类
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function TechBlueNavbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { siteConfig, navMenus, navCategories } = useSiteConfig();
  const { isLoggedIn } = useAuth();

  const siteName = siteConfig?.site_name || siteConfig?.name || "";
  const logo = siteConfig?.logo || "";
  const enableVip = siteConfig?.enable_vip !== false;

  return (
    <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          {logo ? (
            <img src={logo} alt={siteName} className="h-8 w-8 object-contain rounded-lg" />
          ) : siteName ? (
            <div className="h-8 w-8 rounded-md bg-cyan-600 flex items-center justify-center text-white font-bold text-sm">
              {siteName.charAt(0)}
            </div>
          ) : null}
          {siteName && <span className="text-lg font-bold text-gray-900">{siteName}</span>}
        </Link>

        {/* 导航菜单 */}
        <div className="hidden lg:flex items-center gap-0.5">
          <Link to="/" className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-cyan-600 hover:bg-cyan-50/50 rounded-md transition-colors">
            首页
          </Link>
          {navMenus.map((menu) => (
            <Link
              key={menu.id}
              to={menu.type === 'category' ? `/category?cat=${menu.target_id || ''}` :
                   menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                   menu.type === 'link' ? (menu.target_url || '#') : '#'}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-cyan-600 hover:bg-cyan-50/50 rounded-md transition-colors"
            >
              {menu.name}
            </Link>
          ))}
          {navCategories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category?cat=${cat.slug}`}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-cyan-600 hover:bg-cyan-50/50 rounded-md transition-colors"
            >
              {cat.name}
            </Link>
          ))}
          {enableVip && (
            <Link to="/vip" className="px-3 py-1.5 text-sm font-medium text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 rounded-md transition-colors">
              <i className="ri-vip-crown-line mr-1" />VIP
            </Link>
          )}
        </div>

        {/* 右侧 */}
        <div className="flex items-center gap-3">
          {/* 发布按钮 */}
          <button
            onClick={() => navigate("/publish")}
            className="inline-flex items-center gap-1 px-3.5 py-2 text-sm font-bold bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors shadow-sm"
          >
            <i className="ri-add-line text-base" />
            <span className="hidden sm:inline">立即发布</span>
          </button>

          {isLoggedIn ? (
            <Link to="/dashboard" className="text-sm text-gray-600 hover:text-gray-900">会员中心</Link>
          ) : (
            <>
              <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">登录</Link>
              <Link to="/register" className="px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-lg hover:bg-cyan-700 transition-colors">注册</Link>
            </>
          )}
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-1.5 rounded-lg text-gray-600 hover:bg-gray-100">
            <i className={`ri-${isMobileMenuOpen ? "close" : "menu"}-line text-xl`} />
          </button>
        </div>
      </div>

      {/* 移动端菜单 */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white">
          <div className="px-4 py-2 space-y-0.5 max-h-[60vh] overflow-y-auto">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
              <i className="ri-home-4-line mr-2" />首页
            </Link>
            {navMenus.map((menu) => (
              <Link
                key={menu.id}
                to={menu.type === 'category' ? `/category?cat=${menu.target_id || ''}` :
                     menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                     menu.type === 'link' ? (menu.target_url || '#') : '#'}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <i className="ri-menu-line mr-2" />{menu.name}
              </Link>
            ))}
            {navCategories.map((cat) => (
              <Link key={cat.id} to={`/category?cat=${cat.slug}`} onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                <i className="ri-grid-line mr-2" />{cat.name}
              </Link>
            ))}
            {enableVip && (
              <Link to="/vip" onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors">
                <i className="ri-vip-crown-line mr-2" />VIP会员
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
